import React from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "../utils/i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function MobileDrawer({
  open,
  onClose,
  products,
  onCategorySelect,
  onProductSelect,
}) {
  const { t, resolve } = useTranslation();
  const [productsOpen, setProductsOpen] = React.useState(false);
  const [expandedGroup, setExpandedGroup] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setProductsOpen(false);
      setExpandedGroup(null);
    }
  }, [open]);

  const handlePresentationClick = (event) => {
    try {
      const el = event.target;
      if (el && el.closest && el.closest('a,button,[role="button"]')) return;
    } catch (e) {}
    onClose();
  };

  const handleCategorySelect = (path) => {
    onClose();
    onCategorySelect(path);
  };

  const handleProductSelect = (slug) => {
    onClose();
    onProductSelect(slug);
  };

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box
        sx={{ width: 280 }}
        role="presentation"
        onClick={handlePresentationClick}
      >
        <List>
          <ListItemButton component={RouterLink} to="/" onClick={onClose}>
            <ListItemText primary={t("nav.home")} />
          </ListItemButton>

          <ListItemButton onClick={() => setProductsOpen((s) => !s)}>
            <ListItemText primary={t("nav.products")} />
            {productsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={productsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {products.map((group) => (
                <Box key={group.id}>
                  <ListItemButton
                    sx={{
                      mx: 1,
                      my: 0.75,
                      borderRadius: 2,
                      py: 1.1,
                      pl: 1.25,
                      pr: 2,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                    }}
                    onClick={() => {
                      if (group.id === "autec") {
                        handleCategorySelect("/autecproducts");
                        return;
                      }
                      setExpandedGroup((cur) =>
                        cur === group.id ? null : group.id,
                      );
                    }}
                  >
                    <Box
                      component="img"
                      src={group.image}
                      alt={group.title}
                      sx={{
                        width: 30,
                        height: 30,
                        objectFit: "contain",
                        mr: 1.25,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <ListItemText primary={group.title} />
                      {group.id === "autec" && (
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "rgba(0,0,0,0.5)",
                            mt: -0.5,
                          }}
                        >
                          Exclusive Distributor in Sweden Since 2009
                        </Typography>
                      )}
                    </Box>
                    {group.id !== "autec" &&
                      (expandedGroup === group.id ? (
                        <ExpandLess
                          sx={{ opacity: 0.8, mr: 0.25, flexShrink: 0 }}
                        />
                      ) : (
                        <ExpandMore
                          sx={{ opacity: 0.8, mr: 0.25, flexShrink: 0 }}
                        />
                      ))}
                  </ListItemButton>

                  <Collapse
                    in={group.id !== "autec" && expandedGroup === group.id}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {group.items.map((product, index) => (
                        <ListItemButton
                          key={`${group.id}-${product.slug}-${index}`}
                          sx={{
                            mx: 1,
                            my: 0.25,
                            borderRadius: 2,
                            py: 0.9,
                            pl: 3.2,
                            pr: 1,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                          }}
                          onClick={() => handleProductSelect(product.slug)}
                        >
                          <ListItemText
                            primary={
                              resolve(product.title) || resolve(product.name)
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ))}

              <ListItemButton onClick={() => handleCategorySelect("/products")}>
                <ListItemText primary={t("nav.showAllProducts")} />
                <ChevronRightIcon sx={{ fontSize: 24, opacity: 0.8 }} />
              </ListItemButton>
            </List>
          </Collapse>

          <Divider />

          <ListItemButton component={RouterLink} to="/news" onClick={onClose}>
            <ListItemText primary={t("nav.news")} />
          </ListItemButton>
          <ListItemButton component={RouterLink} to="/about" onClick={onClose}>
            <ListItemText primary={t("nav.about")} />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/contact"
            onClick={onClose}
          >
            <ListItemText primary={t("nav.contact")} />
          </ListItemButton>

          <Divider />

          {/* Language switcher */}
          <Box sx={{ px: 2, py: 1.5 }} onClick={(e) => e.stopPropagation()}>
            <LanguageSwitcher />
          </Box>
        </List>
      </Box>
    </Drawer>
  );
}
